/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://material-ui.com/store/items/soft-ui-pro-dashboard/
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

// prop-types is a library for typechecking of props
import PropTypes from 'prop-types';

// react-select components
import Select from 'react-select';

// Soft UI Dashboard PRO React base styles
import colors from 'assets/theme/base/colors';

// Custom styles for SoftSelect
import styles from 'components/SoftSelect/styles';

const SoftSelect = forwardRef(
  (
    {
      size = 'medium',
      error = false,
      success = false,
      options,
      value,
      ...rest
    },
    ref,
  ) => {
    const { light } = colors;
    const { t } = useTranslation();

    // Translate labels for display; the selected value must be translated too, otherwise
    // react-select renders the raw label (e.g. an i18n key) of the value object as-is.
    const translateOption = (option) =>
      option ? { ...option, label: t(option.label) } : option;
    const translatedValue = Array.isArray(value)
      ? value.map(translateOption)
      : translateOption(value);

    return (
      <Select
        {...rest}
        ref={ref}
        value={translatedValue}
        styles={styles(size, error, success)}
        options={options.map(translateOption)}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary25: light.main,
            primary: light.main,
          },
        })}
      />
    );
  },
);

// Typechecking props for the SoftSelect
SoftSelect.propTypes = {
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  error: PropTypes.bool,
  success: PropTypes.bool,
  options: PropTypes.array,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default SoftSelect;
